import { FC, useEffect, useMemo, useState } from 'react';

import { GiftApiPagination } from '@service/admin/components/GiftApiPagination';
import { AddconGoods } from '@shared/api/giftCouponAddcon/giftCouponAddcon.interface';

/** 상품 응답의 발송 타입 → 사람 말 (null = 업체 지정) */
export const addconSendTypeLabel = (sendType: AddconGoods['sendType']): string => {
  switch (sendType) {
    case '1001':
      return 'MMS';
    case '1002':
      return 'LMS';
    case '1004':
      return '알림톡';
    case '2100':
      return '온라인';
    case null:
      return '업체 지정';
    default:
      return String(sendType ?? '-');
  }
};

export const addconValidLabel = (goods: AddconGoods): string => {
  if (goods.validType === 'D' && goods.validTerm) return `${goods.validTerm}일`;
  if (goods.validType === 'M' && goods.validTerm) return `${goods.validTerm}개월`;
  if (goods.validType === 'Y' && goods.validTerm) return `${goods.validTerm}년`;
  if (goods.validType === 'F' && (goods.validStart || goods.validEnd)) {
    return `${goods.validStart ?? ''}~${goods.validEnd ?? ''}`;
  }
  return goods.validType ? `${goods.validType}${goods.validTerm ?? ''}` : '-';
};

export const formatAddconPrice = (price?: number) =>
  typeof price === 'number' ? `${price.toLocaleString()}원` : '-';

/** 검색어로 계약 상품 거르기 — 상품명·브랜드·계약명·계약번호 */
export const filterAddconGoods = (goods: AddconGoods[], query: string): AddconGoods[] => {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return goods;
  return goods.filter((row) => {
    const haystack = [row.noReq, row.goodsName, row.companyName, row.nmReq, row.category]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return words.every((word) => haystack.includes(word));
  });
};

interface Props {
  goods: AddconGoods[];
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  /** 행 선택 버튼 — 있으면 [이 상품] 버튼이 보인다 */
  onSelect?: (goods: AddconGoods) => void;
  selectedNoReq?: string;
  /** 계약번호 복사 버튼 */
  copyable?: boolean;
  /** 표 높이 제한(패널 안에서 쓸 때) */
  compact?: boolean;
  autoFocus?: boolean;
  /** 카탈로그에만 페이지 구분을 적용한다. 연결 패널은 기존 스크롤 유지. */
  pageSize?: number;
}

/**
 * 애드콘 계약 상품 표 — 검색 + (선택 | 복사). 연결 패널과 /gift-api/goods 카탈로그가 공용.
 */
export const AddconGoodsTable: FC<Props> = ({
  goods,
  loading,
  error,
  onRetry,
  onSelect,
  selectedNoReq,
  copyable,
  compact,
  autoFocus,
  pageSize,
}) => {
  const [query, setQuery] = useState('');
  const [copied, setCopied] = useState('');
  const [page, setPage] = useState(1);
  const filtered = useMemo(() => filterAddconGoods(goods, query), [goods, query]);

  useEffect(() => setPage(1), [query]);
  const size = pageSize ?? Math.max(1, filtered.length);
  const currentPage = Math.min(page, Math.max(1, Math.ceil(filtered.length / size)));
  const visible = filtered.slice((currentPage - 1) * size, currentPage * size);

  const copy = async (noReq: string) => {
    try {
      await navigator.clipboard.writeText(noReq);
      setCopied(noReq);
      window.setTimeout(() => setCopied(''), 1500);
    } catch {
      window.prompt('계약번호를 복사하세요', noReq);
    }
  };

  return (
    <div className="min-w-0">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <input
          type="search"
          aria-label="계약 상품 검색"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="상품명·브랜드·계약번호로 찾기"
          autoFocus={autoFocus}
          className="w-full max-w-xs rounded border border-gray-300 bg-white px-3 py-1.5 text-sm focus:border-indigo-500 focus:outline-none"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery('')}
            className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700"
          >
            검색 초기화
          </button>
        )}
        <span role="status" className="text-xs text-gray-500">
          {loading ? '불러오는 중…' : `${filtered.length}개 / 전체 ${goods.length}개`}
        </span>
      </div>
      {error && (
        <p role="alert" className="mb-2 rounded bg-red-50 px-3 py-2 text-xs text-red-700">
          {error}{' '}
          {onRetry && (
            <button type="button" onClick={onRetry} className="ml-1 underline">
              다시 시도
            </button>
          )}
        </p>
      )}
      {pageSize && (
        <div
          className="max-h-[65vh] space-y-2 overflow-auto sm:hidden"
          aria-label="계약 상품 목록"
        >
          {loading && goods.length === 0 && (
            <p className="py-6 text-center text-sm text-gray-500">
              계약 상품을 불러오는 중입니다…
            </p>
          )}
          {!loading && !error && visible.length === 0 && (
            <p className="py-6 text-center text-sm text-gray-500">
              {goods.length
                ? '검색 결과가 없습니다. 검색어를 바꾸거나 초기화해 주세요.'
                : '계약된 상품이 없습니다.'}
            </p>
          )}
          {visible.map((row) => (
            <article
              key={row.noReq}
              className="rounded border border-gray-200 bg-white p-3"
            >
              <div className="flex items-start gap-2">
                {row.goodsImage && (
                  <img
                    src={row.goodsImage}
                    alt=""
                    className="h-10 w-10 shrink-0 rounded object-cover"
                    loading="lazy"
                  />
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-gray-500">
                    {row.companyName ?? '브랜드 미등록'}
                  </p>
                  <h3 className="text-sm font-semibold text-gray-900">
                    {row.goodsName ?? row.nmReq ?? '-'}
                  </h3>
                </div>
                <span className="shrink-0 text-sm font-semibold">
                  {formatAddconPrice(row.cpnPrice)}
                </span>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                유효기간 {addconValidLabel(row)} · {addconSendTypeLabel(row.sendType)}
              </p>
              <div className="mt-2 flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 pt-2">
                <span className="text-xs text-gray-600">
                  계약번호 <span className="font-mono">{row.noReq}</span>
                </span>
                <div className="flex gap-1">
                  {onSelect && (
                    <button
                      type="button"
                      onClick={() => onSelect(row)}
                      className="rounded border border-indigo-300 px-2 py-1 text-xs text-indigo-700"
                    >
                      {selectedNoReq === row.noReq ? '선택됨' : '이 상품'}
                    </button>
                  )}
                  {copyable && (
                    <button
                      type="button"
                      aria-label={`${row.goodsName ?? row.noReq} 계약번호 복사`}
                      onClick={() => void copy(row.noReq)}
                      className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700"
                    >
                      {copied === row.noReq ? '복사됨' : '번호 복사'}
                    </button>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
      <div
        className={`${
          pageSize ? 'hidden max-h-[65vh] sm:block' : ''
        } overflow-auto rounded border border-gray-200 ${compact ? 'max-h-72' : ''}`}
      >
        <table className="min-w-full text-xs">
          <thead className="sticky top-0 z-10 bg-gray-50">
            <tr className="border-b border-gray-200 text-left text-gray-500">
              <th className="whitespace-nowrap px-2 py-1.5">상품명</th>
              <th className="whitespace-nowrap px-2 py-1.5">브랜드</th>
              <th className="whitespace-nowrap px-2 py-1.5">금액</th>
              <th className="whitespace-nowrap px-2 py-1.5">유효</th>
              <th className="whitespace-nowrap px-2 py-1.5">문자</th>
              <th className="whitespace-nowrap px-2 py-1.5">계약번호</th>
              {(onSelect || copyable) && (
                <th className="sticky right-0 bg-gray-50 px-2 py-1.5">
                  <span className="sr-only">작업</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {loading && goods.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-gray-500">
                  계약 상품을 불러오는 중입니다…
                </td>
              </tr>
            )}
            {!loading && !error && filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="px-2 py-4 text-center text-gray-400">
                  {goods.length
                    ? '검색 결과가 없습니다. 다른 검색어를 입력하거나 검색을 초기화해 주세요.'
                    : '계약된 상품이 없습니다.'}
                </td>
              </tr>
            )}
            {visible.map((row) => {
              const selected = selectedNoReq && row.noReq === selectedNoReq;
              return (
                <tr
                  key={row.noReq}
                  className={`border-b border-gray-100 ${
                    selected ? 'bg-indigo-50' : 'hover:bg-gray-50'
                  }`}
                >
                  <td className="min-w-[180px] px-2 py-1.5">
                    <div className="flex items-center gap-2">
                      {row.goodsImage && (
                        <img
                          src={row.goodsImage}
                          alt=""
                          className="h-8 w-8 shrink-0 rounded object-cover"
                          loading="lazy"
                        />
                      )}
                      <span>{row.goodsName ?? row.nmReq ?? '-'}</span>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-2 py-1.5">
                    {row.companyName ?? '-'}
                  </td>
                  <td className="whitespace-nowrap px-2 py-1.5">
                    {formatAddconPrice(row.cpnPrice)}
                  </td>
                  <td className="whitespace-nowrap px-2 py-1.5">
                    {addconValidLabel(row)}
                  </td>
                  <td className="whitespace-nowrap px-2 py-1.5">
                    {addconSendTypeLabel(row.sendType)}
                  </td>
                  <td className="whitespace-nowrap px-2 py-1.5 font-mono">{row.noReq}</td>
                  {(onSelect || copyable) && (
                    <td className="sticky right-0 whitespace-nowrap bg-white px-2 py-1.5">
                      <div className="flex gap-1">
                        {onSelect && (
                          <button
                            type="button"
                            onClick={() => onSelect(row)}
                            className={`rounded border px-2 py-0.5 text-xs ${
                              selected
                                ? 'border-indigo-600 bg-indigo-600 text-white'
                                : 'border-indigo-300 bg-white text-indigo-700 hover:bg-indigo-50'
                            }`}
                          >
                            {selected ? '선택됨' : '이 상품'}
                          </button>
                        )}
                        {copyable && (
                          <button
                            type="button"
                            aria-label={`${row.goodsName ?? row.noReq} 계약번호 복사`}
                            onClick={() => void copy(row.noReq)}
                            className="rounded border border-gray-300 bg-white px-2 py-0.5 text-xs text-gray-700 hover:bg-gray-50"
                          >
                            {copied === row.noReq ? '복사됨' : '번호 복사'}
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <span role="status" className="sr-only">
        {copied ? `계약번호 ${copied} 복사됨` : ''}
      </span>
      {pageSize && (
        <GiftApiPagination
          total={filtered.length}
          page={currentPage}
          pageSize={size}
          onChange={setPage}
        />
      )}
    </div>
  );
};

export default AddconGoodsTable;
